import React, { Component } from 'react'
import { Box, Typography, Button, Card } from '@mui/material'

class ErrorBoundary extends Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      errorMessage: '',
    }
  }

  static getDerivedStateFromError(error) {
    const isProd = process.env.NODE_ENV === 'production'
    return {
      hasError: true,
      errorMessage: isProd
        ? 'A component failed to render or a required resource could not be loaded.'
        : (error?.message || 'An unexpected application error occurred.'),
    }
  }

  componentDidCatch(error, errorInfo) {
    // Sanitized diagnostic logging for monitoring (never logging credentials/tokens)
    const sanitizedReport = {
      name: error?.name || 'Error',
      message: error?.message || 'Unknown render error',
      componentStack: errorInfo?.componentStack?.slice(0, 300),
      timestamp: new Date().toISOString(),
    }
    console.error('[ErrorBoundary caught error]', sanitizedReport)
  }

  handleReload = () => {
    window.location.reload()
  }

  handleRetry = () => {
    this.setState({ hasError: false, errorMessage: '' })
  }

  handleReturnHome = () => {
    const isOffline = typeof window !== 'undefined' && window.location.pathname.startsWith('/offline')
    window.location.href = isOffline ? '/offline' : '/admin'
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#050D06',
            color: '#FFFFFF',
            p: 3,
            fontFamily: 'Montserrat, sans-serif',
          }}
        >
          <Card
            sx={{
              maxWidth: 500,
              width: '100%',
              p: { xs: 3, sm: 4 },
              borderRadius: '24px',
              backgroundColor: '#0A150C',
              border: '1.5px solid rgba(57, 181, 74, 0.3)',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.8)',
              textAlign: 'center',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(239, 68, 68, 0.15)',
                color: '#EF4444',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2.5,
                fontSize: '1.8rem',
                border: '2px solid rgba(239, 68, 68, 0.4)',
              }}
            >
              ⚠️
            </Box>

            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                color: '#FFFFFF',
                mb: 1,
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
              }}
            >
              Something went wrong
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#9CA3AF',
                mb: 3,
                fontSize: '0.88rem',
                lineHeight: 1.6,
              }}
            >
              {this.state.errorMessage ||
                'A component failed to render or a required resource could not be loaded.'}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                onClick={this.handleRetry}
                sx={{
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                  color: '#FFFFFF',
                  borderRadius: '12px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#39B54A',
                    backgroundColor: 'rgba(57, 181, 74, 0.1)',
                  },
                }}
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                onClick={this.handleReturnHome}
                sx={{
                  borderColor: 'rgba(57, 181, 74, 0.4)',
                  color: '#39B54A',
                  borderRadius: '12px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#39B54A',
                    backgroundColor: 'rgba(57, 181, 74, 0.1)',
                  },
                }}
              >
                Return to Portal
              </Button>

              <Button
                variant="contained"
                onClick={this.handleReload}
                sx={{
                  backgroundColor: '#39B54A',
                  color: '#FFFFFF',
                  borderRadius: '12px',
                  px: 2.5,
                  py: 1,
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  textTransform: 'none',
                  boxShadow: '0 4px 14px rgba(57, 181, 74, 0.35)',
                  '&:hover': {
                    backgroundColor: '#2EA03E',
                  },
                }}
              >
                Reload
              </Button>
            </Box>
          </Card>
        </Box>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
